import { isNativeShell } from '../auth/authRedirect';

function csvEscape(value) {
  const text = value == null ? '' : String(value);
  if (/[",\n\r]/.test(text)) return `"${text.replaceAll('"', '""')}"`;
  return text;
}

async function shareFileIfSupported(blob, filename, title) {
  if (!isNativeShell() || typeof File === 'undefined' || !navigator?.share) return false;
  const file = new File([blob], filename, { type: blob.type || 'application/octet-stream' });
  if (navigator.canShare && !navigator.canShare({ files: [file] })) return false;
  await navigator.share({ files: [file], title: title || filename });
  return true;
}

export async function downloadCsv(filename, rows) {
  const data = Array.isArray(rows) ? rows : [];
  if (!data.length) throw new Error('There is no data to export.');
  const headers = [...new Set(data.flatMap(row => Object.keys(row || {})))];
  const lines = [
    headers.map(csvEscape).join(','),
    ...data.map(row => headers.map(header => csvEscape(row?.[header])).join(',')),
  ];
  const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });

  if (await shareFileIfSupported(blob, filename, filename)) return;

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();

  if (isNativeShell()) {
    window.setTimeout(() => window.location.assign(url), 50);
    window.setTimeout(() => URL.revokeObjectURL(url), 60000);
  } else {
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

function htmlEscape(value) {
  return String(value == null ? '' : value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

export async function openPrintableReport({ title, subtitle = '', sections = [] }) {
  const sectionHtml = sections.map(section => {
    const rows = (section.rows || []).map(row => {
      if (Array.isArray(row)) {
        return `<tr>${row.map(cell => `<td>${htmlEscape(cell)}</td>`).join('')}</tr>`;
      }
      return `<div class="line">${htmlEscape(row)}</div>`;
    }).join('');
    const header = Array.isArray(section.headers)
      ? `<thead><tr>${section.headers.map(cell => `<th>${htmlEscape(cell)}</th>`).join('')}</tr></thead>`
      : '';
    const body = Array.isArray(section.headers) ? `<table>${header}<tbody>${rows}</tbody></table>` : rows;
    return `<section><h2>${htmlEscape(section.title || '')}</h2>${body || '<p>None</p>'}</section>`;
  }).join('');

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${htmlEscape(title)}</title><style>
    body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;margin:34px;color:#111827}h1{margin:0 0 6px;font-size:26px}p.meta{margin:0 0 24px;color:#5b6472}section{margin-top:22px;page-break-inside:avoid}h2{font-size:16px;border-bottom:1px solid #d1d5db;padding-bottom:6px}table{width:100%;border-collapse:collapse;font-size:12px}th,td{text-align:left;border-bottom:1px solid #e5e7eb;padding:7px 6px;vertical-align:top}.line{font-size:13px;padding:4px 0}.footer{margin-top:28px;font-size:10px;color:#6b7280}@media print{body{margin:.45in}.no-print{display:none}}</style></head><body>
    <div class="no-print" style="margin-bottom:18px"><button onclick="window.print()">Print / Save PDF</button></div>
    <h1>${htmlEscape(title)}</h1><p class="meta">${htmlEscape(subtitle)}</p>${sectionHtml}<div class="footer">Generated by Line Medic</div>
    </body></html>`;

  const safeTitle = String(title || 'line-medic-report').replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-|-$/g, '') || 'line-medic-report';
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });

  if (await shareFileIfSupported(blob, `${safeTitle}.html`, title)) return;

  const report = window.open('', '_blank', 'noopener,noreferrer');
  if (report) {
    report.document.open();
    report.document.write(html);
    report.document.close();
    report.focus();
    return;
  }

  const url = URL.createObjectURL(blob);
  window.location.assign(url);
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
}
